import type { AxiosResponse } from 'axios'
import type { NSnackbarProps } from '@/modules/global/components/core/NSnackbar/NSnackbar.vue'
import Space, { DigitalSignatureStatus, SpaceStatus } from '@/modules/space/models/Space'
import { usePdfExportStore } from '@/modules/space/stores/PdfExportStore'
import { getApiErrorStatus, isNetworkError } from '@/util/api-error'
import { claimNetworkError } from '@/util/api-error-snackbar'

export interface PdfGenerationResult {
  downloaded: boolean
  finalizationPending: boolean
  finalizationMessage: string | null
  code: string | null
  /** Set when the server renders in the background (202 `pdf-export-queued`): poll it with `pollFn` */
  jobId?: string
}

/** `GET /spaces/{id}/pdf-exports/{jobId}` */
export interface PdfExportStatus {
  status: 'QUEUED' | 'RUNNING' | 'READY' | 'FAILED'
  url?: string | null
  code?: string | null
}

export enum SpacePdfDownloadUiState {
  READY = 'READY',
  PROCESSING = 'PROCESSING'
}

export const PDF_FINALIZATION_ERROR_CODES = new Set(['pdf-finalization-started', 'pdf-finalization-in-progress'])

/**
 * The server renders the PDF in the background and the client polls the job (NS-456): a render can take longer than
 * the load balancer's 60s request timeout, which answered the old synchronous request with a 504.
 */
export const PDF_EXPORT_QUEUED_CODE = 'pdf-export-queued'
const PDF_EXPORT_TIMED_OUT_CODE = 'pdf-generation-timed-out'
export const PDF_EXPORT_POLL_INTERVAL_MS = 3_000
/** With a websocket push to wait for, polling only covers a push that never arrives (dropped socket) */
export const PDF_EXPORT_FALLBACK_POLL_INTERVAL_MS = 15_000
/** Longer than the server's own stale-job cutoff would leave the user waiting on a job it already failed */
export const PDF_EXPORT_POLL_TIMEOUT_MS = 5 * 60_000

type TeamDigitalSignatureSetting = {
  enableDigitalSignature?: boolean
}

/** `TeamStore.currentTeam` is null when the team fetch failed during boot; callers pass it through unguarded. */
type MaybeTeamDigitalSignatureSetting = TeamDigitalSignatureSetting | null | undefined

type TranslateFn = (key: string) => string

export interface SnackbarStoreLike {
  enqueue: (props: NSnackbarProps) => string
  dequeue: (id: string) => void
}

export interface ExecuteSpacePdfDownloadOptions {
  space: Space
  team: MaybeTeamDigitalSignatureSetting
  downloadFn: () => Promise<PdfGenerationResult>
  /** Required for a space whose PDF the server renders in the background, which is every unsigned one */
  pollFn?: (jobId: string) => Promise<PdfExportStatus>
  /** Subscribes to the job's completion push; returns the unsubscribe. Polling then only runs as a slow fallback */
  subscribeFn?: (jobId: string, callback: (status: PdfExportStatus) => void) => () => void
  /** Whether pushes can currently arrive; when not, the status is polled at the short interval instead */
  isLiveFn?: () => boolean
  pollIntervalMs?: number
  pollTimeoutMs?: number
  refreshSpace: (spaceId: string) => Promise<void>
  markFinalizationPending?: (spaceId: string) => void
  snackbarStore: SnackbarStoreLike
  t: TranslateFn
  onDownloaded?: () => void
  /** Set for a single-page export: it is tracked apart from the space's own export (key `spaceId:pageId`) */
  pageId?: string
  /**
   * Aborts the wait silently. Leaving the page does NOT: the export belongs to the app (PdfExportStore), which
   * aborts every export on logout. Only pass one for a caller-specific reason.
   */
  signal?: AbortSignal
}

export function resolvePdfFinalizationMessage(data: unknown): string | null {
  if (data == null) {
    return null
  }
  if (typeof data === 'string' && data.trim()) {
    return data
  }
  if (typeof data === 'object' && data !== null && 'message' in data) {
    const m = (data as { message: unknown }).message
    if (typeof m === 'string' && m.trim()) {
      return m
    }
  }
  return null
}

export function resolvePdfFinalizationCode(data: unknown): string | null {
  if (data == null || typeof data !== 'object') {
    return null
  }
  const code = (data as { code?: unknown }).code
  return typeof code === 'string' && code.trim() ? code : null
}

/** Parses the `{ code, description }` / `{ message }` shape shared by the backend's ErrorBody responses. */
export function resolveApiErrorCode(error: unknown): string | null {
  const err = error as { response?: { data?: unknown } }
  const data = err.response?.data
  if (data == null || typeof data !== 'object') {
    return null
  }
  const code = (data as { code?: unknown }).code
  return typeof code === 'string' && code.trim() ? code : null
}

/** Parses the `{ description }` / `{ message }` shape shared by the backend's ErrorBody responses. */
export function resolveApiErrorDescription(error: unknown): string | null {
  const err = error as { response?: { data?: unknown } }
  const data = err.response?.data
  if (data == null || typeof data !== 'object') {
    return null
  }
  const body = data as { description?: unknown; message?: unknown }
  if (typeof body.description === 'string' && body.description.trim()) {
    return body.description
  }
  if (typeof body.message === 'string' && body.message.trim()) {
    return body.message
  }
  return null
}

export function isPdfFinalizationEligible(space: Space, team: MaybeTeamDigitalSignatureSetting): boolean {
  return space.status === SpaceStatus.FULLY_SIGNED && !!team?.enableDigitalSignature
}

export function deriveSpacePdfDownloadUiState(
  space: Space,
  team: MaybeTeamDigitalSignatureSetting
): SpacePdfDownloadUiState {
  if (!isPdfFinalizationEligible(space, team)) {
    return SpacePdfDownloadUiState.READY
  }

  const status = (space.digitalSignatureStatus?.trim() || DigitalSignatureStatus.NOT_STARTED) as DigitalSignatureStatus
  if (status === DigitalSignatureStatus.PENDING || status === DigitalSignatureStatus.PROCESSING) {
    return SpacePdfDownloadUiState.PROCESSING
  }

  return SpacePdfDownloadUiState.READY
}

export function isSpacePdfDownloadProcessing(
  space: Space | null | undefined,
  team: MaybeTeamDigitalSignatureSetting
): boolean {
  if (!space || !team) {
    return false
  }
  return deriveSpacePdfDownloadUiState(space, team) === SpacePdfDownloadUiState.PROCESSING
}

export function resolvePdfFinalizationErrorMessage(error: unknown): string | null {
  return resolveApiErrorDescription(error)
}

export function resolvePdfDownloadErrorMessage(
  error: unknown,
  fallback: string,
  options: { finalizationEligible: boolean; finalizationFallback: string }
): string {
  const code = resolveApiErrorCode(error)
  if (code != null && PDF_FINALIZATION_ERROR_CODES.has(code)) {
    if (!options.finalizationEligible) {
      return fallback
    }
    return resolveApiErrorDescription(error) ?? options.finalizationFallback
  }
  return resolveApiErrorDescription(error) ?? fallback
}

/** Same body parsing as finalize errors — for GET /spaces/.../pdf and .../pages/.../pdf failures. */
export function resolvePdfDownloadErrorMessageOrFallback(error: unknown, fallback: string): string {
  return resolvePdfFinalizationErrorMessage(error) ?? fallback
}

/**
 * Interprets a PDF-endpoint Axios response: 202 means finalization is pending,
 * any other success status triggers the download via the returned URL.
 */
export function handlePdfResponse(response: AxiosResponse): PdfGenerationResult {
  const code = resolvePdfFinalizationCode(response.data)
  const jobId = (response.data as { jobId?: unknown } | null)?.jobId
  // Without a job id there is nothing to poll: fall through to the 202 handling below, which reports a failure
  if (response.status === 202 && code === PDF_EXPORT_QUEUED_CODE && typeof jobId === 'string') {
    return {
      downloaded: false,
      finalizationPending: false,
      finalizationMessage: null,
      code,
      jobId
    }
  }
  if (response.status === 202) {
    return {
      downloaded: false,
      finalizationPending: true,
      finalizationMessage: resolvePdfFinalizationMessage(response.data),
      code
    }
  }
  const url = typeof response.data === 'string' ? response.data : (response.data as { url?: string } | null)?.url
  navigateToDownload(url)
  return {
    downloaded: true,
    finalizationPending: false,
    finalizationMessage: null,
    code: null
  }
}

interface EnterPdfFinalizationPendingOptions {
  spaceId: string
  message: string
  refreshSpace: (spaceId: string) => Promise<void>
  markFinalizationPending?: (spaceId: string) => void
  snackbarStore: SnackbarStoreLike
}

/** Refresh store state and show a deduped snackbar so download UI enters PROCESSING. */
export async function enterPdfFinalizationPending(options: EnterPdfFinalizationPendingOptions): Promise<void> {
  const { spaceId, message, refreshSpace, markFinalizationPending, snackbarStore } = options
  try {
    await refreshSpace(spaceId)
  } catch {
    // Backend already indicated finalization is pending; still drive local UI into PROCESSING.
  }
  markFinalizationPending?.(spaceId)
  snackbarStore.enqueue({
    label: message,
    leadingIcon: true,
    variant: 'warning',
    dedupeKey: `pdf-finalization-pending-${spaceId}`
  })
}

class PdfExportFailedError extends Error {
  constructor(readonly code: string | null) {
    super(code ?? 'pdf-export-failed')
  }
}

/** The caller's signal aborted: the user left, so the outcome is no longer theirs to see */
class PdfExportCancelledError extends Error {
  constructor() {
    super('pdf-export-cancelled')
  }
}

/**
 * Waits for a background export to have a URL, by a pushed status when `subscribeFn` is given and by polling otherwise
 * (or as the fallback). Rejects with PdfExportFailedError when it fails or runs too long.
 */
async function waitForPdfExport(
  jobId: string,
  pollFn: (jobId: string) => Promise<PdfExportStatus>,
  getIntervalMs: () => number,
  timeoutMs: number,
  subscribeFn?: ExecuteSpacePdfDownloadOptions['subscribeFn'],
  signal?: AbortSignal,
  /** Hands out a function that makes the wait read the job right now (after the socket was restarted) */
  registerCheckNow?: (checkNow: () => void) => void
): Promise<string> {
  const deadline = Date.now() + timeoutMs
  let droppedPoll = false
  let pushed: PdfExportStatus | null = null
  let wake: (() => void) | null = null
  const unsubscribe = subscribeFn?.(jobId, status => {
    pushed = status
    wake?.()
  })
  // The job may have finished before the subscription was live, so the first read happens straight away
  let firstRead = !!subscribeFn
  const onAbort = () => wake?.()
  signal?.addEventListener('abort', onAbort)
  // No push is pending, so waking the sleep makes the loop below poll
  registerCheckNow?.(() => wake?.())
  try {
    for (;;) {
      if (signal?.aborted) throw new PdfExportCancelledError()
      if (!pushed && !firstRead) {
        await new Promise<void>(resolve => {
          // Chosen per wait: the socket may have dropped or come back since the last one
          const timer = setTimeout(resolve, getIntervalMs())
          wake = () => {
            clearTimeout(timer)
            resolve()
          }
        })
        wake = null
        if (signal?.aborted) throw new PdfExportCancelledError()
      }
      firstRead = false
      let job: PdfExportStatus | null = pushed
      pushed = null
      if (!job) {
        try {
          job = await pollFn(jobId)
        } catch (error) {
          if (signal?.aborted) throw new PdfExportCancelledError()
          // One dropped poll is not a failed export: the job keeps running on the server
          if (isNetworkError(error) && !droppedPoll) {
            claimNetworkError(error)
            droppedPoll = true
            continue
          }
          throw error
        }
      }
      droppedPoll = false
      // A job that finished while the user was leaving must not navigate them away
      if (signal?.aborted) throw new PdfExportCancelledError()
      if (job.status === 'READY' && job.url) return job.url
      if (job.status === 'FAILED') throw new PdfExportFailedError(job.code ?? null)
      if (Date.now() >= deadline) throw new PdfExportFailedError(PDF_EXPORT_TIMED_OUT_CODE)
    }
  } finally {
    // Every exit (failure, timeout, abort) must drop the listener and the socket subscription
    signal?.removeEventListener('abort', onAbort)
    unsubscribe?.()
  }
}

/**
 * A PDF request that got no response while the browser is online timed out somewhere on the way (the load balancer's
 * 504 carries no CORS headers, so axios sees no response at all): say so instead of blaming the connection.
 */
function isPdfTimeout(error: unknown, waitingForJob: boolean): boolean {
  if (error instanceof PdfExportFailedError) return error.code === PDF_EXPORT_TIMED_OUT_CODE
  if (getApiErrorStatus(error) === 504) return true
  // While waiting for the job a dropped connection is just that: the request that could time out has long returned
  return !waitingForJob && isNetworkError(error) && navigator.onLine !== false
}

/** Only web URLs may be navigated to: the link also arrives over the websocket push, which is not validated upstream */
function navigateToDownload(url: unknown): void {
  let parsed: URL | null = null
  try {
    parsed = typeof url === 'string' && url ? new URL(url, window.location.origin) : null
  } catch {
    parsed = null
  }
  if (!parsed || (parsed.protocol !== 'https:' && parsed.protocol !== 'http:')) {
    throw new PdfExportFailedError('pdf-export-invalid-url')
  }
  window.location.href = url as string
}

export async function executeSpacePdfDownload(options: ExecuteSpacePdfDownloadOptions): Promise<void> {
  const {
    space,
    team,
    downloadFn,
    pollFn,
    subscribeFn,
    isLiveFn,
    refreshSpace,
    markFinalizationPending,
    snackbarStore,
    t,
    onDownloaded,
    pageId
  } = options
  const finalizationEligible = isPdfFinalizationEligible(space, team)
  const generationFailedLabel = t('signatureList.pdfGenerationFailed')
  const finalizationFallbackLabel = t('signatureList.pdfFinalizationInProgress')

  if (options.signal?.aborted) {
    return
  }

  if (deriveSpacePdfDownloadUiState(space, team) === SpacePdfDownloadUiState.PROCESSING) {
    return
  }

  // One export per target; other spaces/pages export in parallel, untouched by this one
  const exportStore = usePdfExportStore()
  const exportKey = pageId ? `${space.id}:${pageId}` : space.id
  const appSignal = exportStore.begin(exportKey, { spaceId: space.id, pageId })
  if (!appSignal) {
    return
  }
  // Stops on the app-level abort (logout) or the caller's own signal
  const stop = new AbortController()
  const abortWait = () => stop.abort()
  appSignal.addEventListener('abort', abortWait)
  options.signal?.addEventListener('abort', abortWait)
  const signal = stop.signal

  const loadingSnackbarId = snackbarStore.enqueue({
    label: t('signatureList.pdfGenerationActive'),
    showLoadingIndicator: true,
    variant: 'success',
    autoHide: false
  })

  let waitingForJob = false
  try {
    let result = await downloadFn()
    if (signal?.aborted) return
    if (result.jobId) {
      waitingForJob = true
      if (!pollFn) throw new PdfExportFailedError(null)
      const url = await waitForPdfExport(
        result.jobId,
        pollFn,
        () =>
          options.pollIntervalMs ??
          (subscribeFn && isLiveFn?.() ? PDF_EXPORT_FALLBACK_POLL_INTERVAL_MS : PDF_EXPORT_POLL_INTERVAL_MS),
        options.pollTimeoutMs ?? PDF_EXPORT_POLL_TIMEOUT_MS,
        subscribeFn,
        signal,
        checkNow => exportStore.registerNudge(exportKey, checkNow)
      )
      navigateToDownload(url)
      result = { ...result, downloaded: true }
    }
    if (result.downloaded) {
      snackbarStore.enqueue({
        label: t('signatureList.pdfDownloadingNow'),
        leadingIcon: true,
        variant: 'success'
      })
      onDownloaded?.()
      return
    }

    if (result.finalizationPending) {
      if (finalizationEligible) {
        await enterPdfFinalizationPending({
          spaceId: space.id,
          message: result.finalizationMessage || finalizationFallbackLabel,
          refreshSpace,
          markFinalizationPending,
          snackbarStore
        })
        return
      }

      snackbarStore.enqueue({
        label: generationFailedLabel,
        leadingIcon: true,
        variant: 'warning'
      })
    }
  } catch (error) {
    // The user left: no snackbar for a page they are no longer on
    if (signal?.aborted) return
    if (isPdfTimeout(error, waitingForJob)) {
      // Claimed in the same tick as the rejection, before ApiClient's queued "network error" snackbar fires
      claimNetworkError(error)
      snackbarStore.enqueue({
        label: t('signatureList.pdfGenerationTimedOut'),
        leadingIcon: true,
        variant: 'warning'
      })
      return
    }
    if (error instanceof PdfExportFailedError) {
      snackbarStore.enqueue({
        label: generationFailedLabel,
        leadingIcon: true,
        variant: 'warning'
      })
      return
    }
    const code = resolveApiErrorCode(error)
    if (code != null && PDF_FINALIZATION_ERROR_CODES.has(code) && finalizationEligible) {
      await enterPdfFinalizationPending({
        spaceId: space.id,
        message: resolveApiErrorDescription(error) ?? finalizationFallbackLabel,
        refreshSpace,
        markFinalizationPending,
        snackbarStore
      })
      return
    }

    snackbarStore.enqueue({
      label: resolvePdfDownloadErrorMessage(error, generationFailedLabel, {
        finalizationEligible,
        finalizationFallback: finalizationFallbackLabel
      }),
      leadingIcon: true,
      variant: 'warning'
    })
  } finally {
    snackbarStore.dequeue(loadingSnackbarId)
    appSignal.removeEventListener('abort', abortWait)
    options.signal?.removeEventListener('abort', abortWait)
    exportStore.end(exportKey)
  }
}
